import { useMemo } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ListChecks } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { PLAYER_TYPES } from "@convex/_lib/playerTypes";
import { useAdminAuth } from "@/features/admin/hooks/useAdminAuth";
import { AdminShell } from "@/features/admin/shell/AdminShell";
import { AdminPageHeader } from "@/features/admin/shell/AdminPageHeader";
import { PlayerKindSelect, type PlayerKind } from "@/features/admin/players/components/PlayerKindSelect";
import { Skeleton } from "@/shared/ui/Skeleton";
import { DataTable } from "@/shared/ui/DataTable";
import type { DataTableColumn, FilterDef } from "@/shared/ui/DataTable";
import { EmptyState } from "@/shared/ui/EmptyState";
import { useT } from "@/shared/i18n/LocaleContext";

export const Route = createFileRoute("/_adminGuarded/admin/players/")({
  component: AdminPlayersPage,
});

type FlatPlayer = {
  _id: Id<"players">;
  fullName: string;
  isActive: boolean;
  position: PlayerKind;
  teamId: Id<"knbsbTeams">;
  teamName: string;
  teamShortName: string;
};

function AdminPlayersPage() {
  const t = useT();
  const { state } = useAdminAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const groups = useQuery(
    api.admin.listPlayersByTeamWithPositions,
    token ? { token } : "skip",
  );

  const flat = useMemo<ReadonlyArray<FlatPlayer> | undefined>(() => {
    if (!groups) return undefined;
    return groups.flatMap((g) =>
      g.players.map((p) => ({
        _id: p._id,
        fullName: p.fullName,
        isActive: p.isActive,
        position: p.position,
        teamId: g.team._id,
        teamName: g.team.name,
        teamShortName: g.team.shortName ?? g.team.name,
      })),
    );
  }, [groups]);

  const teamOptions = useMemo(
    () =>
      (groups ?? []).map((g) => ({
        value: g.team._id,
        label: g.team.name,
      })),
    [groups],
  );

  const columns = useMemo<ReadonlyArray<DataTableColumn<FlatPlayer>>>(
    () => [
      {
        id: "name",
        accessorKey: "fullName",
        header: t("admin.players.columns.name"),
        cell: ({ row }) => (
          <span className={row.original.isActive ? "text-ink font-medium" : "text-muted line-through"}>
            {row.original.fullName}
          </span>
        ),
      },
      {
        id: "team",
        accessorKey: "teamName",
        header: t("admin.players.columns.team"),
        cell: ({ row }) => (
          <span className="text-sm text-muted">{row.original.teamShortName}</span>
        ),
      },
      {
        id: "teamId",
        accessorKey: "teamId",
        header: "",
        enableSorting: false,
        cell: () => null,
      },
      {
        id: "kind",
        accessorKey: "position",
        header: t("admin.players.columns.kind"),
        cell: ({ row }) => (
          <PlayerKindSelect
            token={token}
            playerId={row.original._id}
            value={row.original.position}
            className="min-w-[10rem]"
          />
        ),
      },
      {
        id: "gameLog",
        header: "Game log",
        align: "right",
        enableSorting: false,
        cell: ({ row }) => (
          <Link
            to="/admin/players/$playerId"
            params={{ playerId: row.original._id }}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-sm border border-border bg-surface px-3 text-sm font-medium text-ink transition-colors hover:border-ink hover:bg-surface-2"
            aria-label={`Open game log for ${row.original.fullName}`}
          >
            <ListChecks size={14} />
            <span>Log</span>
          </Link>
        ),
      },
    ],
    [t, token],
  );

  const filters = useMemo<ReadonlyArray<FilterDef>>(
    () => [
      {
        id: "teamId",
        label: t("admin.players.teamLabel"),
        type: "select",
        options: teamOptions,
      },
      {
        id: "kind",
        label: t("admin.players.kindLabel"),
        type: "select",
        options: [
          { value: PLAYER_TYPES.HITTER, label: t("admin.players.kinds.positionPlayer") },
          { value: PLAYER_TYPES.PITCHER, label: t("admin.players.kinds.pitcher") },
        ],
      },
    ],
    [t, teamOptions],
  );

  return (
    <AdminShell breadcrumbs={[{ label: t("admin.players.title") }]}>
      <AdminPageHeader
        title={t("admin.players.title")}
        subtitle={t("admin.players.subtitle")}
      />

      {flat === undefined ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : (
        <DataTable<FlatPlayer>
          columns={columns}
          data={flat}
          rowKey={(r) => r._id}
          searchPlaceholder={t("admin.players.searchPlaceholder")}
          filters={filters}
          hiddenColumnIds={["teamId"]}
          groupBy={(r) => r.teamName}
          pageSize={50}
          empty={
            <EmptyState
              title={t("admin.players.emptyTitle")}
              description={t("admin.players.emptyDescription")}
            />
          }
        />
      )}
    </AdminShell>
  );
}
